import { useMutation } from '@tanstack/react-query'
import { getHeaders, handleErrors } from 'services/api/utils'
import { findOutWhichEnv, getEnvPath } from 'utils/env'

const env = findOutWhichEnv()
const BASE_URL = `${getEnvPath(env)}/1.0/api`

/**
 * Fetches pricing analytics data based on provided filters.
 * @param {Object} payload - The filters for the pricing analytics report.
 * @returns {Promise<Object>} - A promise that resolves to the fetched data.
 */
export async function generatePricingAnalyticDataByFilters(payload) {
  const response = await fetch(
    `${BASE_URL}/report/pricinganalytics`,
    getHeaders('POST', { ...payload, version: '2' }),
  ).catch(() => {
    throw new Error('Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.')
  })
  const isHttpClientError = response.status >= 400 && response.status < 500
  const flagClientError = (error) => Object.assign(error, isHttpClientError ? { isClientError: true } : {})

  const body = await response.json().catch(() => {
    throw flagClientError(new Error('Não foi possível ler a resposta do servidor. Tente novamente.'))
  })

  try {
    return handleErrors(body)
  } catch (error) {
    const isBodyClientError = body?.status >= 400 && body?.status < 500
    throw isBodyClientError ? Object.assign(error, { isClientError: true }) : flagClientError(error)
  }
}

/** A validation error (4xx) will not fix itself: only other failures are retried, once. */
export const shouldRetryPricingRequest = (failureCount, error) => failureCount < 1 && !error?.isClientError

function usePricingAnalyticReport() {
  return useMutation({
    mutationKey: ['pricing-analytic-report-data'],
    mutationFn: generatePricingAnalyticDataByFilters,
    retry: shouldRetryPricingRequest,
  })
}

export default usePricingAnalyticReport
